/**
 * widgets/org-switcher/ui/OrgSwitcher — Organization switcher dropdown.
 *
 * Renders a dropdown in the app shell header that:
 *   - Shows the name of the currently active organization.
 *   - Lists all the user's organizations as clickable items.
 *   - Switches the active org via `useActiveOrg().setActiveOrg()`.
 *   - Offers a "Create organization" item that opens a dialog with the create form.
 *
 * When the organizations module is DISABLED (MODULE_ORGANIZATIONS=false),
 * this component renders null — the app behaves as a per-user (single-tenant) app.
 *
 * FSD: widgets may import from features, entities, and shared only.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Button } from '@/shared/ui/button';
import { useActiveOrg, useMyOrgsQuery } from '@/entities/organization';
import type { Organization } from '@/entities/organization';
import { CreateOrganizationForm } from '@/features/create-organization';
import { MODULE_ORGANIZATIONS } from '@/shared/config/env';

/**
 * The actual switcher UI — rendered only when the organizations module is enabled.
 */
function OrgSwitcherInner() {
  const { t } = useTranslation('organizations');
  const { activeOrg, setActiveOrg, isPending } = useActiveOrg();
  const { data: orgsData, isLoading } = useMyOrgsQuery();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const orgs = orgsData?.results ?? [];

  function handleSelectOrg(org: Organization) {
    if (org.id === activeOrg?.id) {
      setDropdownOpen(false);
      return;
    }
    void setActiveOrg(org);
    setDropdownOpen(false);
  }

  function handleCreateSuccess(org: Organization) {
    void setActiveOrg(org);
    setIsDialogOpen(false);
  }

  return (
    <>
      <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="max-w-[180px] truncate"
            aria-label={t('switcher.label')}
            data-testid="org-switcher-trigger"
            disabled={isPending}
          >
            {isLoading
              ? t('switcher.loading')
              : (activeOrg?.name ?? t('switcher.noOrgs'))}
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="start" className="w-56" data-testid="org-switcher-menu">
          <DropdownMenuLabel>{t('switcher.activeLabel')}</DropdownMenuLabel>
          <DropdownMenuSeparator />

          {isLoading ? (
            <DropdownMenuItem disabled>{t('switcher.loading')}</DropdownMenuItem>
          ) : orgs.length === 0 ? (
            <DropdownMenuItem disabled>{t('switcher.noOrgs')}</DropdownMenuItem>
          ) : (
            orgs.map((org) => (
              <DropdownMenuItem
                key={org.id}
                onSelect={() => handleSelectOrg(org)}
                data-testid={`org-switcher-item-${org.id}`}
                aria-current={org.id === activeOrg?.id ? 'true' : undefined}
                className={org.id === activeOrg?.id ? 'font-semibold' : undefined}
              >
                {org.name}
              </DropdownMenuItem>
            ))
          )}

          <DropdownMenuSeparator />
          <DropdownMenuItem
            onSelect={() => {
              setDropdownOpen(false);
              setIsDialogOpen(true);
            }}
            data-testid="org-switcher-create"
          >
            {t('switcher.create')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Create Organization Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('create.title')}</DialogTitle>
            <DialogDescription>{t('create.description')}</DialogDescription>
          </DialogHeader>
          <CreateOrganizationForm
            onSuccess={handleCreateSuccess}
            onError={() => {
              // Error is shown in the form itself — no need to close on error.
            }}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}

/**
 * Module-guarded org switcher.
 * Renders nothing when the organizations module is disabled.
 */
export function OrgSwitcher() {
  if (!MODULE_ORGANIZATIONS) {
    return null;
  }
  return <OrgSwitcherInner />;
}
